import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { BadgeWithButton } from "@/components/base/badges/badges";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { UnavailableOption } from "@/components/filters/shared";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cx } from "@/utils/cx";

export type MultiSelectValue = Array<{ id: string; label: string }>;

export type MultiSelectOption = { id: string; label: string; unavailableReason?: string };

export interface SearchableMultiSelectProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  label: string;
  search: string;
  setSearch: (search: string) => void;
  options: MultiSelectOption[] | undefined;
  /**
   * Renders options under labelled sections instead of a flat list — takes
   * precedence over `options` when given.
   */
  groups?: Array<{ heading: string; options: MultiSelectOption[] }>;
  isLoading?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  emptyState?: ReactNode;
  loadingMessage?: string;
  disabled?: boolean;
}

// Memoized selected item badge to prevent re-rendering all badges when one is removed
const SelectedItemBadge = memo(
  ({ item, onRemove }: { item: { id: string; label: string }; onRemove: (id: string) => void }) => {
    const handleRemove = useCallback(() => onRemove(item.id), [onRemove, item.id]);

    return (
      <BadgeWithButton
        type="pill-color"
        size="md"
        color="black"
        buttonLabel={`Remove ${item.label} filter`}
        onButtonClick={handleRemove}
      >
        <span className="text-sm max-w-32 truncate">{item.label}</span>
      </BadgeWithButton>
    );
  },
);

SelectedItemBadge.displayName = "SelectedItemBadge";

// Memoized command option item
const OptionItem = memo(
  ({ option, onSelect }: { option: MultiSelectOption; onSelect: (id: string, label: string) => void }) => {
    const handleSelect = useCallback(() => onSelect(option.id, option.label), [onSelect, option.id, option.label]);

    if (option.unavailableReason) {
      return (
        <CommandItem value={option.id} disabled className="p-0 data-[disabled=true]:pointer-events-auto">
          <UnavailableOption semantics="inherit" reason={option.unavailableReason} className="flex-1 px-2 py-1.5">
            {option.label}
          </UnavailableOption>
        </CommandItem>
      );
    }

    return (
      <CommandItem value={option.id} onSelect={handleSelect}>
        {option.label}
      </CommandItem>
    );
  },
);

OptionItem.displayName = "OptionItem";

const SearchableMultiSelectInner = <TFieldValues extends FieldValues>({
  field,
  label,
  search,
  setSearch,
  options,
  groups,
  isLoading = false,
  placeholder = "Search...",
  emptyMessage = "No options found",
  emptyState,
  loadingMessage = "Loading...",
  disabled = false,
}: SearchableMultiSelectProps<TFieldValues>) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open, setSearch]);

  const value = field.value as MultiSelectValue;

  // Memoize the set of selected IDs for O(1) lookup
  const selectedIds = useMemo(() => new Set(value?.map((v) => v.id) ?? []), [value]);

  // Memoize the remove handler
  const handleRemove = useCallback(
    (id: string) => {
      field.onChange(value.filter((v) => v.id !== id));
    },
    [field, value],
  );

  // Memoize the select handler
  const handleSelect = useCallback(
    (id: string, itemLabel: string) => {
      if (!selectedIds.has(id)) {
        field.onChange([...value, { id, label: itemLabel }]);
      }
      setOpen(false);
    },
    [field, value, selectedIds, setOpen],
  );

  // Memoize the input change handler
  const handleInputChange = useCallback(
    (newValue: string) => {
      setSearch(newValue);
    },
    [setSearch],
  );

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!disabled) {
        setOpen(newOpen);
      }
    },
    [disabled],
  );

  return (
    <div className={cx("flex flex-col gap-1", disabled && "opacity-50 pointer-events-none")}>
      <Label className="text-sm">{label}</Label>
      <div className="flex flex-col gap-2">
        <Popover open={disabled ? false : open} onOpenChange={handleOpenChange}>
          <PopoverTrigger className="w-full" disabled={disabled}>
            <Input
              placeholder={placeholder}
              inputClassName="text-sm"
              value={search}
              onChange={handleInputChange}
              isDisabled={disabled}
            />
          </PopoverTrigger>
          <PopoverContent
            className="p-0 w-[var(--radix-popover-trigger-width)]"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false}>
              <CommandList>
                <CommandEmpty>{isLoading ? loadingMessage : (emptyState ?? emptyMessage)}</CommandEmpty>
                {groups ? (
                  groups.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map((option) => (
                        <OptionItem key={option.id} option={option} onSelect={handleSelect} />
                      ))}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup>
                    {options?.map((option) => (
                      <OptionItem key={option.id} option={option} onSelect={handleSelect} />
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="flex flex-row flex-wrap gap-1">
          {value?.map((item) => (
            <SelectedItemBadge key={item.id} item={item} onRemove={handleRemove} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const SearchableMultiSelect = memo(SearchableMultiSelectInner) as typeof SearchableMultiSelectInner;
